import { OverageStatus, ServiceLineStatus, ServiceLineType } from "@/shared/types/serviceLineTypes";
import { TerminalType } from "@/shared/types/userTerminalTypes";

/** Tailwind v3 default 500 palette — single source for charts and aligned UI colors. */
export const TAILWIND_COLORS = {
    green: '#22c55e',
    orange: '#f97316',
    red: '#ef4444',
    gray: '#6b7280',
    blue: '#3b82f6',
    yellow: '#eab308',
    purple: '#a855f7',
    indigo: '#6366f1',
    black: '#64748b',
    amber: '#fbbf24',
    standardData: '#6b7280',
    orbiusBlue: '#005584',
} as const;

export type TailwindColorKey = keyof typeof TAILWIND_COLORS;

export const DEFAULT_COLOR_KEY: TailwindColorKey = 'black';

/** Type guard for palette keys stored on domain maps. */
export function isTailwindColorKey(color: string | undefined): color is TailwindColorKey {
    return color != null && color in TAILWIND_COLORS;
}

/** Normalize any palette key (or unknown string) to a valid `TailwindColorKey`. */
export function resolveTailwindColorKey(color: string | undefined): TailwindColorKey {
    return isTailwindColorKey(color) ? color : DEFAULT_COLOR_KEY;
}

/** Hex fill for Recharts/SVG and inline `color` / `background` styles. */
export function getChartFill(color: string | undefined): string {
    return TAILWIND_COLORS[resolveTailwindColorKey(color)];
}

/**
 * Keys that aren't a Tailwind color scale (no `*-500` utilities).
 * `black` maps to slate-500 to match TAILWIND_COLORS.black (#64748b).
 */
export const TEXT_COLOR_CLASS: Record<TailwindColorKey, string> = {
    green: 'text-green-500',
    orange: 'text-orange-500',
    red: 'text-red-500',
    gray: 'text-gray-500',
    blue: 'text-blue-500',
    yellow: 'text-yellow-500',
    purple: 'text-purple-500',
    indigo: 'text-indigo-500',
    amber: 'text-amber-500',
    black: 'text-black',
    standardData: 'text-gray-500',
    orbiusBlue: 'text-[#005584]',
};

export const BG_COLOR_CLASS: Record<TailwindColorKey, string> = {
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500',
    gray: 'bg-gray-500',
    blue: 'bg-blue-500',
    yellow: 'bg-yellow-500',
    purple: 'bg-purple-500',
    indigo: 'bg-indigo-500',
    amber: 'bg-amber-500',
    black: 'bg-black',
    standardData: 'bg-gray-500',
    orbiusBlue: 'bg-[#005584]',
};

export const BORDER_COLOR_CLASS: Record<TailwindColorKey, string> = {
    green: 'border-green-500',
    orange: 'border-orange-500',
    red: 'border-red-500',
    gray: 'border-gray-500',
    blue: 'border-blue-500',
    yellow: 'border-yellow-500',
    purple: 'border-purple-500',
    indigo: 'border-indigo-500',
    amber: 'border-amber-500',
    black: 'border-black',
    standardData: 'border-gray-500',
    orbiusBlue: 'border-[#005584]',
};

/** Tailwind text utility matching the same 500 shade as `getChartFill`. */
export function getTextColorClass(color: string | undefined): string {
    return TEXT_COLOR_CLASS[resolveTailwindColorKey(color)];
}

/** Tailwind background utility matching the same 500 shade as `getChartFill`. */
export function getBgColorClass(color: string | undefined): string {
    return BG_COLOR_CLASS[resolveTailwindColorKey(color)];
}

/** Tailwind border utility matching the same 500 shade as `getChartFill`. */
export function getBorderColorClass(color: string | undefined): string {
    return BORDER_COLOR_CLASS[resolveTailwindColorKey(color)];
}

/** Badge styles (white label on saturated 500 background). */
export function getBadgeColorClasses(color: string | undefined): string {
    if (!isTailwindColorKey(color)) {
        return 'text-muted-foreground shadow-sm bg-muted';
    }
    return `text-white shadow-sm ${getBgColorClass(color)}`;
}

/** Utility to get a color key from a map of keys to color keys. */
export function colorKeyFromMap<T extends string | number>(
    map: Record<T, string>,
    key: T,
): TailwindColorKey {
    return resolveTailwindColorKey(map[key]);
}